import type { QueryClient } from '@tanstack/react-query';
import { generationKeys } from './keys';

/** Деталь одной записи перечитываем не чаще раза в 1,5 с (ТЗ главной § 22.9): у подборки
 *  на 20 слов SSE иначе дал бы 20 перечиток по ~100 КБ подряд. */
const DETAIL_THROTTLE_MS = 1500;

const lastRun = new Map<string, number>();
const timers = new Map<string, ReturnType<typeof setTimeout>>();
/** Перечитка нужна, а запрос детали уже идёт: отписка от ожидания его конца. */
const waiting = new Map<string, () => void>();

const findDetail = (qc: QueryClient, id: string) =>
  qc.getQueryCache().find({ queryKey: generationKeys.byId(id), exact: true });

/**
 * Сводка записи сменилась (статус, счётчики или жетоны) — перечитать её деталь, если состав открывали.
 * Троттлинг с последним вызовом в конце: итоговое состояние всегда дочитывается. Идущий запрос
 * не отменяем — частые события не дали бы ему дойти; но он мог уйти до события (или это первая
 * загрузка состава, данных ещё нет), поэтому сразу после него — ещё одна перечитка.
 */
export function refreshGenerationDetail(qc: QueryClient, id: string): void {
  if (!findDetail(qc, id)) return;
  if (timers.has(id) || waiting.has(id)) return;
  const wait = (lastRun.get(id) ?? 0) + DETAIL_THROTTLE_MS - Date.now();
  if (wait <= 0) runRefresh(qc, id);
  else
    timers.set(
      id,
      setTimeout(() => {
        timers.delete(id);
        runRefresh(qc, id);
      }, wait),
    );
}

function runRefresh(qc: QueryClient, id: string): void {
  const query = findDetail(qc, id);
  if (!query) return;
  if (query.getObserversCount() === 0) {
    // Запись свёрнута: перечитается, когда её раскроют. Сейчас — только пометка «устарела».
    void qc.invalidateQueries({ queryKey: generationKeys.byId(id), exact: true, refetchType: 'none' });
    return;
  }
  if (query.state.fetchStatus !== 'idle') {
    const off = qc.getQueryCache().subscribe((event) => {
      if (event.query !== query) return;
      if (event.type !== 'removed' && event.query.state.fetchStatus !== 'idle') return;
      off();
      waiting.delete(id);
      if (event.type !== 'removed') refreshGenerationDetail(qc, id);
    });
    waiting.set(id, off);
    return;
  }
  lastRun.set(id, Date.now());
  void qc.refetchQueries({ queryKey: generationKeys.byId(id), exact: true, type: 'active' }, { cancelRefetch: false });
}

/** Смена пользователя: отложенные перечитки прошлого ученика не нужны. */
export function cancelGenerationDetailRefresh(): void {
  timers.forEach((timer) => clearTimeout(timer));
  timers.clear();
  waiting.forEach((off) => off());
  waiting.clear();
  lastRun.clear();
}
